import React, { useContext, useMemo, useCallback } from 'react';
import { View, StyleSheet, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useExploreStore, useAuthStore, useModalStore } from '@/store';
import { trackEvent } from '@/constants/utils/analytics';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import PageSearchInput from '@/components/Inputs/PageSearchInput';
import ExploreCategoryPills from '@/components/Explore/ExploreCategoryPills';
import ExploreSortDropdown from '@/components/Dropdowns/ExploreSortDropdown';

const isWeb = Platform.OS === 'web';

export const ExplorePageChromeContext = React.createContext(null);

/**
 * Stable list header for MasonryFlashList — same component reference avoids remounting
 * the template carousel when parent re-renders on filter changes.
 */
function ExplorePageListHeader({
  hideSearch = false,
  /** When true, hides `ExploreCategoryPills` and the active-category chip (e.g. catalog toolbar shows category). */
  hideCategoryPills = false,
  hideSortDropdown = false,
} = {}) {
  const ctx = useContext(ExplorePageChromeContext);
  const { colors } = useTheme();
  const categories = useExploreStore(s => s.categories);
  const filters = useExploreStore(s => s.filters);
  const applyFiltersAndRefetch = useExploreStore(s => s.actions.applyFiltersAndRefetch);
  const token = useAuthStore(s => s.token);
  const isGhost = useAuthStore(s => s.user?.flags?.isGhost);
  const openModal = useModalStore(s => s.actions.openModal);

  const activeCategoryName = useMemo(() => {
    if (!filters.category || !categories?.length) return null;
    const cat = categories.find(c => c.slug === filters.category);
    return cat ? cat.name : filters.category;
  }, [filters.category, categories]);

  const handleClearCategory = useCallback(() => {
    trackEvent('explore', 'explore_filter_category', null);
    applyFiltersAndRefetch({ category: null });
    ctx?.syncFiltersToUrl?.({ category: null });
  }, [applyFiltersAndRefetch, ctx]);

  const handleClearModel = useCallback(() => {
    trackEvent('explore', 'explore_filter_model', null);
    applyFiltersAndRefetch({ model: null });
    ctx?.syncFiltersToUrl?.({ model: null });
  }, [applyFiltersAndRefetch, ctx]);

  const handleCategoryChange = useCallback((slug) => {
    trackEvent('explore', 'explore_filter_category', slug);
    applyFiltersAndRefetch({ category: slug });
    ctx?.syncFiltersToUrl?.({ category: slug });
  }, [applyFiltersAndRefetch, ctx]);

  const handleSortChange = useCallback((sortId) => {
    if (sortId === 'favorites') {
      if (!token || isGhost) {
        trackEvent('explore', 'explore_auth_required', 'favorites_filter');
        openModal(isGhost ? 'GhostSignupModal' : 'SignupModal');
        return;
      }
      if (filters.favorites) return;
      trackEvent('explore', 'explore_filter_favorites', 'on');
      applyFiltersAndRefetch({ favorites: true });
      ctx?.syncFiltersToUrl?.({ favorites: true });
      return;
    }
    trackEvent('explore', 'explore_filter_sort', sortId);
    const updates = { sort: sortId };
    if (filters.favorites) updates.favorites = false;
    applyFiltersAndRefetch(updates);
    ctx?.syncFiltersToUrl?.(updates);
  }, [applyFiltersAndRefetch, ctx, token, isGhost, openModal, filters.favorites]);

  if (!ctx) return null;

  const { searchText, onSearchTextChange } = ctx;

  return (
    <View style={styles.controls}>
      <View style={styles.filtersRow}>
        {!hideSortDropdown && (
          <ExploreSortDropdown
            activeSort={filters.sort}
            filterFavorites={filters.favorites}
            onChangeSort={handleSortChange}
          />
        )}
        {activeCategoryName && !hideCategoryPills && (
          <Pressable
            onPress={handleClearCategory}
            style={[styles.activeCategoryChip, { backgroundColor: colors.solid_color_secondary }]}
          >
            <Icon name="tag" size={13} color={colors.text_color_secondary} />
            <Text size="s" weight="medium" color="primary" numberOfLines={1}>
              {activeCategoryName}
            </Text>
            <Icon name="xmark" size={10} color={colors.text_color_tertiary} />
          </Pressable>
        )}
        {filters.model && (
          <Pressable
            onPress={handleClearModel}
            style={[styles.activeCategoryChip, { backgroundColor: colors.solid_color_secondary }]}
          >
            <Icon name="microchip" size={13} color={colors.text_color_secondary} />
            <Text size="s" weight="medium" color="primary" numberOfLines={1}>
              {filters.model}
            </Text>
            <Icon name="xmark" size={10} color={colors.text_color_tertiary} />
          </Pressable>
        )}
      </View>

      {!hideSearch && (
        <PageSearchInput
          value={searchText}
          onChangeText={onSearchTextChange}
          placeholder="Search explore..."
          containerStyle={styles.searchContainer}
        />
      )}

      {!hideCategoryPills && (
        <View style={styles.categoryRow}>
          <ExploreCategoryPills
            categories={categories}
            activeSlug={filters.category}
            activeMediaType={filters.mediaType}
            onSelect={handleCategoryChange}
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  controls: {
    gap: 10,
    paddingTop: 12,
    paddingBottom: 8,
  },
  filtersRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    paddingHorizontal: 16,
    flexWrap: 'wrap',
  },
  searchContainer: {
    paddingVertical: 0,
    paddingBottom: 0,
  },
  activeCategoryChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    height: 31,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    maxWidth: 180,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  categoryRow: {
    gap: 8,
  },
});

export default React.memo(ExplorePageListHeader);
